import { useState } from 'react'
import { useParams, Link } from 'react-router'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '../../lib/api.js'
import { Spinner, Empty, fmtNum } from '../../components/ui.jsx'
import { Estructura } from '../../components/Estructura.jsx'
import { ConfirmarBorrado } from '../../components/ConfirmarBorrado.jsx'
import { NuevaTabla } from '../../components/NuevaTabla.jsx'
import { useAyuda } from '../../lib/ayuda.jsx'
import { AyudaBaseDatos } from '../../components/AyudaContrato.jsx'

export function Tables() {
  // La parte del contrato que toca a esta página, en el panel de ayuda («?» de la cabecera).
  useAyuda(() => ({ titulo: 'Base de datos · el contrato', nodo: <AyudaBaseDatos /> }), [])

  const { slug } = useParams()
  const qc = useQueryClient()
  const [sel, setSel] = useState(null)
  const [creando, setCreando] = useState(false)
  const [aBorrar, setABorrar] = useState(null)
  const [nuevaCol, setNuevaCol] = useState({ nombre: '', tipo: 'text', nulo: true, porDefecto: '' })

  const tipos = useQuery({ queryKey: ['tipos'], queryFn: api.tipos, staleTime: Infinity })
  const refrescar = () => {
    qc.invalidateQueries({ queryKey: ['tables', slug] })
    if (sel) qc.invalidateQueries({ queryKey: ['table', slug, sel.schema, sel.name] })
  }
  const ddl = useMutation({ mutationFn: ({ que, body }) => api.ddl(slug, que, body), onSuccess: refrescar })
  const borrarTabla = useMutation({
    mutationFn: (body) => api.ddl(slug, 'table/drop', body),
    onSuccess: () => { setABorrar(null); setSel(null); refrescar() },
  })
  const list = useQuery({ queryKey: ['tables', slug], queryFn: () => api.tables(slug) })
  const detail = useQuery({
    queryKey: ['table', slug, sel?.schema, sel?.name],
    queryFn: () => api.table(slug, sel.schema, sel.name),
    enabled: Boolean(sel),
  })

  return (
    <>
      <div className="section-head">
        <div>
          <h1 className="h1">Explorador de tablas</h1>
          <p className="lede" style={{ marginBottom: 0 }}>
            Las filas son una estimación (se refresca a los pocos segundos de escribir), no un
            recuento exacto: un <code>COUNT(*)</code> en una tabla grande bloquearía la vista.
          </p>
        </div>
        <button className="btn primary" onClick={() => setCreando((v) => !v)}>
          {creando ? 'Cancelar' : 'Nueva tabla'}
        </button>
      </div>

      {creando && (
        <NuevaTabla tipos={tipos.data ?? []} ocupado={ddl.isPending}
                    error={ddl.error?.body?.detail}
                    onCrear={(body) => ddl.mutate({ que: 'table', body },
                                                  { onSuccess: () => setCreando(false) })} />
      )}

      {list.isPending && <Spinner />}
      {list.isError && <div className="alert error">No se pudo leer la base de datos.</div>}
      {list.data?.length === 0 && (
        <Empty title="Sin tablas" action={<Link className="btn secondary sm" to={`/p/${slug}/sql`}>Abrir editor SQL</Link>}>
          Esta base de datos no tiene ninguna tabla todavía.
        </Empty>
      )}

      {list.data?.length > 0 && (
        <div className="card">
          <div className="table-scroll">
            <table className="t">
              <thead>
                <tr><th>Tabla</th><th>Tipo</th><th style={{ textAlign: 'right' }}>Filas ≈</th>
                    <th style={{ textAlign: 'right' }}>Tamaño</th></tr>
              </thead>
              <tbody>
                {list.data.map((t) => (
                  <tr key={`${t.schema}.${t.name}`} className="link"
                      onClick={() => setSel(sel?.name === t.name && sel?.schema === t.schema ? null : t)}>
                    <td>
                      <span className="mono">{t.schema === 'public' ? t.name : `${t.schema}.${t.name}`}</span>
                      {t.comment && <div style={{ color: 'var(--fg-3)', fontSize: 12 }}>{t.comment}</div>}
                    </td>
                    <td style={{ color: 'var(--fg-2)' }}>{t.kind}</td>
                    <td className="num">{fmtNum(t.estimatedRows)}</td>
                    <td className="num" style={{ color: 'var(--fg-2)' }}>{t.size}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {sel && (
        <div className="section">
          <div className="section-head">
            <h2 className="h2" style={{ margin: 0 }}>
              <span className="mono">{sel.schema}.{sel.name}</span>
            </h2>
            <button className="btn sm danger" onClick={() => setABorrar(sel)}>Borrar tabla</button>
          </div>
          {detail.isPending && <Spinner />}
          {detail.data && (
            <>
              <div className="card">
                <div className="card-head"><span className="h2" style={{ fontSize: 14 }}>Columnas</span></div>
                <div className="table-scroll">
                  <table className="t">
                    <thead><tr><th>Nombre</th><th>Tipo</th><th>Nulo</th><th>Por defecto</th><th /></tr></thead>
                    <tbody>
                      {detail.data.columns.map((c) => (
                        <tr key={c.name}>
                          <td className="mono">
                            {c.name}
                            {c.primaryKey && <span className="badge" style={{ marginLeft: 8 }}>PK</span>}
                          </td>
                          <td style={{ color: 'var(--fg-2)' }} className="mono">{c.type}</td>
                          <td style={{ color: 'var(--fg-2)' }}>{c.notNull ? 'no' : 'sí'}</td>
                          <td className="mono ellipsis" style={{ color: 'var(--fg-3)' }}>{c.default ?? '—'}</td>
                          <td style={{ textAlign: 'right' }}>
                            {!c.primaryKey && (
                              <button className="btn sm danger" onClick={() => setABorrar({
                                columna: c.name, schema: sel.schema, name: sel.name,
                              })}>Borrar</button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="card-pad" style={{ borderTop: '1px solid var(--border-soft)' }}>
                <div className="field-label">Añadir columna</div>
                <div className="toolbar">
                  <input className="input" placeholder="nombre" value={nuevaCol.nombre} spellCheck="false"
                         style={{ width: 180, fontFamily: 'var(--mono)' }}
                         onChange={(e) => setNuevaCol({ ...nuevaCol, nombre: e.target.value.trim() })} />
                  <select className="input" value={nuevaCol.tipo}
                          onChange={(e) => setNuevaCol({ ...nuevaCol, tipo: e.target.value })}>
                    {(tipos.data ?? []).map((t) => <option key={t}>{t}</option>)}
                  </select>
                  <input className="input" placeholder="por defecto (opcional)" value={nuevaCol.porDefecto}
                         spellCheck="false" style={{ width: 200, fontFamily: 'var(--mono)' }}
                         onChange={(e) => setNuevaCol({ ...nuevaCol, porDefecto: e.target.value })} />
                  <label style={{ display: 'flex', gap: 6, alignItems: 'center', fontSize: 13, color: 'var(--fg-2)' }}>
                    <input type="checkbox" checked={!nuevaCol.nulo}
                           onChange={(e) => setNuevaCol({ ...nuevaCol, nulo: !e.target.checked })} /> obligatoria
                  </label>
                  <button className="btn primary" disabled={!nuevaCol.nombre || ddl.isPending}
                          onClick={() => ddl.mutate({
                            que: 'column',
                            body: { esquema: sel.schema, tabla: sel.name, ...nuevaCol },
                          }, { onSuccess: () => setNuevaCol({ nombre: '', tipo: 'text', nulo: true, porDefecto: '' }) })}>
                    Añadir
                  </button>
                </div>
                <div className="field-hint">
                  Marcar «obligatoria» sobre una tabla con filas falla si no pones un valor por defecto.
                </div>
                {ddl.isError && <div className="alert error" style={{ marginTop: 10 }}>
                  {ddl.error.body?.detail ?? ddl.error.message}
                </div>}
              </div>

              <Estructura slug={slug} esquema={sel.schema} tabla={sel.name} columnas={detail.data.columns} />

            </>
          )}
        </div>
      )}
      <ConfirmarBorrado
        abierto={!!aBorrar}
        nombre={aBorrar?.columna ?? aBorrar?.name ?? ''}
        titulo={aBorrar?.columna ? 'Borrar la columna' : 'Borrar la tabla'}
        ocupado={borrarTabla.isPending || ddl.isPending}
        onCancelar={() => setABorrar(null)}
        onConfirmar={() => (aBorrar.columna
          ? ddl.mutate({ que: 'column/drop', body: { esquema: aBorrar.schema, tabla: aBorrar.name, nombre: aBorrar.columna } },
                       { onSuccess: () => setABorrar(null) })
          : borrarTabla.mutate({ esquema: aBorrar.schema, nombre: aBorrar.name }))}
      >
        {aBorrar?.columna ? (
          <p style={{ marginTop: 0 }}>
            Se borra la columna <span className="mono">{aBorrar.columna}</span> de{' '}
            <span className="mono">{aBorrar.schema}.{aBorrar.name}</span>, <strong>con todos sus
            datos</strong>.
          </p>
        ) : (
          <p style={{ marginTop: 0 }}>
            Se borra la tabla <span className="mono">{aBorrar?.schema}.{aBorrar?.name}</span> con
            todas sus filas, índices y disparadores. <strong>No hay copia de seguridad</strong>.
          </p>
        )}
      </ConfirmarBorrado>
    </>
  )
}
